import React, {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useState,
  useContext,
} from 'react';

import {
  NewGnbInfo,
  GnbPcInfo,
  GnbPcMegaMenuInfo,
  LargeCategory,
  MiddleCategory,
  PcGnbCategoryInfo,
  SearchBar,
} from '@wconcept/wconcept-common/models/renewal/gnb';
import { Product } from '@wconcept/wconcept-ui-renewal/@types/product';
import {
  Brand,
  SearchAutoCompleteContent,
  SearchInitInfo,
} from '@wconcept/wconcept-ui-renewal/@types/search';
import { LnbV2Menu } from '@wconcept/wconcept-common/models/renewal/lnb';

import ContainerLayout from '@wconcept/wconcept-ui-renewal/components/layouts/ContainerLayout';
import ContTitleDesktop from '@wconcept/wconcept-ui-renewal/components/molecules/_desktop/ContTitleDesktop';
import FooterDesktop from '@wconcept/wconcept-ui-renewal/components/organisms/_desktop/FooterDesktop';
import HeaderDesktop from '@wconcept/wconcept-ui-renewal/components/organisms/_desktop/HeaderDesktop';
import FloatUtilsDesktop from '@wconcept/wconcept-ui-renewal/components/organisms/_desktop/FloatUtilsDesktop';
import Text from '@wconcept/wconcept-ui-renewal/components/atoms/Text';
import { IconArrowG4 } from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import {
  SearchKeywordInfo,
  PopularKeywordInfo,
} from '@wconcept/wconcept-common/models/renewal/search';
import type { AgentOnBoardingYn } from '@wconcept/wconcept-common/models/renewal/datadog';

import * as S from './style';

interface IProps {
  /** className */
  className?: string;
  /** title */
  title?: string;
  /** contTitsuffix */
  contTitsuffix?: React.ReactNode;
  /** breadcrumbNode : 타이틀 위 카테고리 path 영역 */
  breadcrumbNode?: React.ReactNode;
  /** children */
  children?: React.ReactNode;
  /** megaMenu */
  megaMenu?: GnbPcMegaMenuInfo;
  gnbPcInfo?: GnbPcInfo;
  /** gnbInfo */
  gnbInfo: NewGnbInfo[];
  /** bodyRef */
  bodyRef?: RefObject<HTMLDivElement> | null | undefined;
  /** isNativeAppBoth */
  isNativeAppBoth: boolean;
  /** headerRef */
  headerRef?: RefObject<HTMLDivElement> | null | undefined;
  /** keyword */
  keyword?: string;
  /** isLogin */
  isLogin: boolean;
  /** isDesktop */
  isDesktop: boolean;
  /** isHistoryBtn */
  isHistoryBtn?: boolean;
  /** isStyleClipAddBtn */
  isStyleClipAddBtn?: boolean;
  /** searchPopularWords */
  searchPopularWords?: PopularKeywordInfo;
  /** searchAutoKeywordInfo */
  searchAutoKeywordInfo?: SearchAutoCompleteContent;
  /** recentSearchKeywordsList */
  recentSearchKeywordsList?: string;
  /** basketCount */
  basketCount: number;
  /** searchInitInfo */
  searchInitInfo: SearchKeywordInfo;
  /** historyImg */
  historyImg?: string;
  /** isShortFormBtnTooltip */
  isShortFormBtnTooltip?: boolean;
  /** setTooltipShown */
  setTooltipShown?: (value: string) => void;
  /** styleClipTooltipPoint */
  styleClipTooltipPoint?: number;
  /** 기획전 eventTitle */
  eventTitle?: string;
  /** 기획전 shareItem */
  shareItem?: React.ReactNode;
  /** 기획전 viewProductBtn */
  viewProductBtn?: React.ReactNode;
  /** 기획전 homeUrl */
  homeUrl?: string;
  /** 기획전 eventUrl */
  eventUrl?: string;
  /** historyBtnRs */
  historyBtnRs?: string;
  /** getHistoryImg */
  getHistoryImg?: () => void;
  /** onClickGnbMenu */
  onClickGnbMenu: (gnbInfo: NewGnbInfo, i: number) => void;
  /** onClickLogo */
  onClickLogo: () => void;
  /** onClickJoin */
  onClickJoin: () => void;
  /** onClickLogin */
  onClickLogin: () => void;
  /** onClickLogout */
  onClickLogout: () => void;
  /** onClickHeart */
  onClickHeart: () => void;
  /** onClickMyPage */
  onClickMyPage: () => void;
  /** onClickCart */
  onClickCart: () => void;
  /** onClickMediumCategory */
  onClickMediumCategory: (pcGnbCategoryInfo: PcGnbCategoryInfo) => void;
  /** onClickLargeCategory */
  onClickLargeCategory: (
    pcGnbCategoryInfo: PcGnbCategoryInfo,
    largeCategory: LargeCategory
  ) => void;
  /** onClickMiddleCategory */
  onClickMiddleCategory: (
    pcGnbCategoryInfo: PcGnbCategoryInfo,
    largeCategory: LargeCategory,
    middleCategory: MiddleCategory
  ) => void;
  /** onPressEnter */
  onPressEnter: (value: string, recentKeywords: string[]) => void;
  /** onClickAutoSearchResult */
  onClickAutoSearchResult: (
    value: string,
    type: string,
    recentKeywordResult: string[],
    item?: Brand,
    no?: number
  ) => void;
  /** onKeywordClick */
  onKeywordClick: (
    param: string,
    type: string,
    recentKeywords: string[],
    no: number
  ) => void;
  /** requestAutoComplete */
  requestAutoComplete?: (keyword: string) => void;
  /** requestSearchPopularWords */
  requestSearchPopularWords: () => void;
  /** onClickLnbMenu */
  onClickLnbMenu?: (lnbData: LnbV2Menu, type: string) => void;
  /** onSearchClick */
  onSearchClick?: (
    e: HTMLInputElement,
    recentKeywordsList: string[],
    searchInitInfo?: SearchKeywordInfo
  ) => void;
  /** onClickHistory */
  onClickHistory?: () => void;
  onClickStyleClipAddBtn?: () => void;
  onClickTop?: () => void;
  agentOnBoardingYn?: AgentOnBoardingYn;
}

const MainLayoutDesktop = ({
  className,
  children,
  title,
  contTitsuffix,
  megaMenu,
  breadcrumbNode,
  gnbPcInfo,
  gnbInfo,
  basketCount,
  bodyRef,
  headerRef,
  isNativeAppBoth,
  keyword,
  isDesktop,
  isHistoryBtn,
  isStyleClipAddBtn,
  styleClipTooltipPoint,
  isLogin,
  searchPopularWords,
  searchAutoKeywordInfo,
  recentSearchKeywordsList,
  searchInitInfo,
  historyImg,
  isShortFormBtnTooltip,
  setTooltipShown,
  eventTitle,
  shareItem,
  viewProductBtn,
  homeUrl,
  eventUrl,
  historyBtnRs,
  getHistoryImg,
  onClickLogo,
  onClickLnbMenu,
  onClickJoin,
  onClickLogin,
  onClickLogout,
  onClickHeart,
  onClickMyPage,
  onClickCart,
  onClickMediumCategory,
  onClickLargeCategory,
  onClickMiddleCategory,
  onPressEnter,
  onClickAutoSearchResult,
  onKeywordClick,
  requestAutoComplete,
  requestSearchPopularWords,
  onClickGnbMenu,
  onSearchClick,
  onClickHistory,
  onClickStyleClipAddBtn,
  onClickTop,
  agentOnBoardingYn,
}: IProps) => {
  const cName = classNameBind(['main-layout-desktop', className || '']);
  const [isBtnTopShow, setIsBtnTopShow] = useState(false);

  // const { openModal } = useContext(ModalsContext); // 모달 Context
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment

  useEffect(() => {
    getHistoryImg?.();
  }, []);

  // 탑버튼, 히스토리버튼 노출
  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop } = document.documentElement;
      const scrolledToTop = scrollTop === 0;
      setIsBtnTopShow(!scrolledToTop);
    };

    const scrollListener = () => {
      window.addEventListener('scroll', handleScroll);
    };
    scrollListener();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // EVENT HANDLER : onClickTopBtn
  const onClickTopBtn = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (onClickTop) onClickTop();
  }, [onClickTop]);

  return (
    <S.MainLayoutDesktop className={cName} ref={bodyRef}>
      <HeaderDesktop
        ref={headerRef}
        megaMenu={megaMenu}
        gnbPcInfo={gnbPcInfo}
        gnbInfo={gnbInfo}
        keyword={keyword}
        isNativeAppBoth={isNativeAppBoth}
        recentSearchKeywordsList={recentSearchKeywordsList || ''}
        searchAutoKeywordInfo={
          searchAutoKeywordInfo ?? ({} as SearchAutoCompleteContent)
        }
        searchPopularWords={searchPopularWords || ({} as PopularKeywordInfo)}
        searchInitInfo={searchInitInfo}
        basketCount={basketCount}
        isDesktop={isDesktop}
        isLogin={isLogin}
        onClickLogo={onClickLogo}
        onClickJoin={onClickJoin}
        onClickLogin={onClickLogin}
        onClickLogout={onClickLogout}
        onClickHeart={onClickHeart}
        onClickMyPage={onClickMyPage}
        onClickCart={onClickCart}
        onClickMediumCategory={onClickMediumCategory}
        onClickLargeCategory={onClickLargeCategory}
        onClickMiddleCategory={onClickMiddleCategory}
        onPressEnter={onPressEnter}
        onClickAutoSearchResult={onClickAutoSearchResult}
        onKeywordClick={onKeywordClick}
        requestAutoComplete={requestAutoComplete}
        requestSearchPopularWords={requestSearchPopularWords}
        onClickGnbMenu={onClickGnbMenu}
        onClickLnbMenu={onClickLnbMenu}
        onSearchClick={onSearchClick}
      />
      {breadcrumbNode}
      {title && <ContTitleDesktop title={title} suffix={contTitsuffix} />}
      <ContainerLayout>
        {/* 기획전 타이틀 영역 */}
        {eventTitle && (
          <>
            <ul className="location">
              <li>
                <a href={homeUrl}>HOME</a>
                <IconArrowG4 />
              </li>
              <li>
                <a href={eventUrl}>EVENT</a>
                <IconArrowG4 />
              </li>
              <li>{eventTitle}</li>
            </ul>
            <div className="event-info">
              <Text size={26}>{eventTitle}</Text>
              {shareItem}
            </div>
          </>
        )}

        {children}

        {/* 기획전 상품보기 버튼 */}
        {viewProductBtn}
      </ContainerLayout>
      <FloatUtilsDesktop
        btnTopShow={isBtnTopShow}
        rs={historyBtnRs}
        isHistoryBtn={isHistoryBtn}
        isStyleClipAddBtn={isStyleClipAddBtn}
        historyImg={historyImg}
        isLogin={isLogin}
        isShortFormBtnTooltip={isShortFormBtnTooltip}
        styleClipTooltipPoint={styleClipTooltipPoint}
        setTooltipShown={setTooltipShown}
        onClickTop={onClickTopBtn}
        onClickHistory={onClickHistory}
        getHistoryImg={getHistoryImg}
        onClickStyleClipAddBtn={onClickStyleClipAddBtn}
      />
      <FooterDesktop agentOnBoardingYn={agentOnBoardingYn} />
    </S.MainLayoutDesktop>
  );
};

export default memo(MainLayoutDesktop);
